/* eslint-disable @typescript-eslint/no-explicit-any */
import api from '@/api';
import { PageResult } from '@/core/request/PageResult';

import { API_BASE_URL } from '..';

import {
  AccountPayable,
  AccountPayableGenerateGroupingRequest,
  AccountPayableGroupingRequest,
  AccountPayableGroupingResponse,
  AccountPayableRangeDateRequest,
  AccountPayableRangeDateResponse,
  AccountPayableReleaseRequest,
  AccountPayableReleaseUpdateRequest,
  AccountPayableReturnListInstallments,
  InstallmentCancelRequest,
  InstallmentList,
  InstallmentReverseRequest,
  InstallmentRenegotiateRequest,
  InstallmentSettleMultipleRequest,
  InstallmentSettlePartiallyRequest,
  InstallmentSettleSingleRequest,
  InstallmentSplitRequest,
  ListInstallmentsQuery,
  GenerateFinancialByPurchase,
  AccountPayableListByPurchase,
  InstallmentRenegotiateResponse,
  AccountPayableReverseMultipleInstallmentRequest,
  AccountPayableDeleteMultipleInstallmentRequest,
  AccountPayableListGroupingResponse,
  AccountPayableArchive,
  AccountPayableArchiveRemoveRequest,
  AccountPayableArchiveCreateRequest,
  AccountPayableIncludeTagRequest,
} from './AccountPayable.types';

const baseUrl = `${API_BASE_URL}/AccountPayable`;

export async function listInstallments(params?: ListInstallmentsQuery) {
  const result = (await api.get(baseUrl, params)) as unknown;

  return result as PageResult<InstallmentList>;
}

export async function createInstallment(
  obj: AccountPayableReleaseRequest,
): Promise<AccountPayable> {
  const result = (await api.post(`${baseUrl}`, obj, null)) as unknown;

  return result as AccountPayable;
}

export async function getListInstallments(accountPayableId: number) {
  const result = (await api.get(
    `${baseUrl}/${accountPayableId}`,
    null,
  )) as unknown;

  return result as AccountPayableReturnListInstallments;
}

export async function updateAccountPayableAndInstallmentPayable(
  obj: AccountPayableReleaseUpdateRequest,
) {
  const result = (await api.put(
    `${baseUrl}/${obj.accountPayableId}/InstallmentPayableId/${obj.installmentPayableId}`,
    obj,
    null,
  )) as unknown;

  return result as AccountPayable;
}

export async function cancelInstallmentPayable(obj: InstallmentCancelRequest) {
  const result = (await api.put(
    `${baseUrl}/CancelInstallment`,
    obj,
    null,
  )) as unknown;

  return result;
}

export async function settledSingleInstallmentPayable(
  obj: InstallmentSettleSingleRequest,
) {
  const result = (await api.put(
    `${baseUrl}/SettleSingleInstallment`,
    obj,
    null,
  )) as unknown;

  return result;
}

export async function settledPartiallyInstallmentPayable(
  obj: InstallmentSettlePartiallyRequest,
) {
  const result = (await api.put(
    `${baseUrl}/PartiallySettleInstallment`,
    obj,
    null,
  )) as unknown;

  return result;
}

export async function settledMultipleInstallmentPayable(
  obj: InstallmentSettleMultipleRequest,
) {
  const result = (await api.put(
    `${baseUrl}/SettleMultipleInstallment`,
    obj,
    null,
  )) as unknown;

  return result;
}

export async function splitInstallmentPayable(obj: InstallmentSplitRequest) {
  const result = (await api.put(
    `${baseUrl}/SplitInstallment`,
    obj,
    null,
  )) as unknown;

  return result;
}

export async function reverseInstallmentPayable(
  obj: InstallmentReverseRequest,
) {
  const result = (await api.put(
    `${baseUrl}/ReverseInstallment`,
    obj,
    null,
  )) as unknown;

  return result;
}

export async function renegotiateAccountPayable(
  obj: InstallmentRenegotiateRequest,
): Promise<InstallmentRenegotiateResponse> {
  const result = (await api.post(
    `${baseUrl}/Renegotiate`,
    obj,
    null,
  )) as unknown;

  return result as InstallmentRenegotiateResponse;
}

export async function accountPayableListGrouping(
  params?: AccountPayableGroupingRequest,
) {
  const result = (await api.get(
    `${baseUrl}/ListAccountPayableForGrouping`,
    params,
  )) as unknown;

  return result as PageResult<AccountPayableGroupingResponse>;
}

export async function generateGrouping(
  obj: AccountPayableGenerateGroupingRequest,
): Promise<AccountPayable> {
  const result = (await api.post(
    `${baseUrl}/GenerateGrouping`,
    obj,
    null,
  )) as unknown;

  return result as AccountPayable;
}

export async function accountPayableRangeDate(
  params?: AccountPayableRangeDateRequest,
) {
  const result = (await api.get(`${baseUrl}/RangeDate`, params)) as unknown;

  return result as AccountPayableRangeDateResponse[];
}

export async function generateFinancialByPurchase(
  obj: GenerateFinancialByPurchase,
) {
  const result = (await api.post(
    `${baseUrl}/purchase/generate`,
    obj,
    null,
  )) as unknown;

  return result;
}

export async function accountPayableListByPurchase(
  params?: AccountPayableListByPurchase,
) {
  const result = (await api.get(`${baseUrl}/purchase/list`, params)) as unknown;

  return result as PageResult<InstallmentList>;
}

export async function updateAccountPayableReverseMultipleInstallment(
  obj: AccountPayableReverseMultipleInstallmentRequest,
) {
  const result = (await api.put(
    `${baseUrl}/ReverseMultipleInstallment`,
    obj,
    null,
  )) as unknown;

  return result as PageResult<InstallmentList>;
}

export async function updateAccountPayableDeleteMultipleInstallment(
  obj: AccountPayableDeleteMultipleInstallmentRequest,
) {
  const result = (await api.put(
    `${baseUrl}/CancelInstallments`,
    obj,
    null,
  )) as unknown;

  return result as PageResult<InstallmentList>;
}

export async function accountPayableListGroupingById(
  accountPayableId?: number,
) {
  const result = (await api.get(
    `${baseUrl}/ListGroupings/${accountPayableId}`,
    null,
  )) as unknown;

  return result as AccountPayableListGroupingResponse[];
}

export async function accountPayableArchiveCreate(
  data: AccountPayableArchiveCreateRequest,
) {
  const formData = new FormData();

  data.files.forEach((file) => {
    formData.append(`files`, file);
  });

  const result = (await api.post(
    `${baseUrl}/${data.accountPayableId}/Archives`,
    formData,
    null,
    {
      contentType: 'multpart/form-data',
    },
  )) as unknown;

  return result as AccountPayableArchive[];
}

export async function accountPayableArchiveList(accountPayableId?: number) {
  const result = (await api.get(
    `${baseUrl}/${accountPayableId}/Archives`,
    null,
  )) as unknown;

  return result as AccountPayableArchive[];
}

export async function accountPayableArchiveRemove(
  params: AccountPayableArchiveRemoveRequest,
) {
  const result = (await api.remove(
    `${baseUrl}/${params.accountPayableId}/Archives/${params.fileId}`,
    {},
    null,
  )) as unknown;

  return result as void;
}

export async function accountPayableIncludeTag({
  accountPayableId,
  installmentPayableId,
  ...data
}: AccountPayableIncludeTagRequest) {
  const result = (await api.patch(
    `${baseUrl}/${accountPayableId}/installmentPayableId/${installmentPayableId}/tag`,
    data,
    null,
  )) as unknown;

  return result;
}
